import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Crown, 
  Check, 
  X,
  AlertTriangle,
  ArrowRight,
  Users,
  Phone,
  BarChart3,
  Clock,
  Zap,
  Building2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

// Plan features configuration
const PLAN_FEATURES = {
  free: {
    name: 'Free',
    price: 0,
    color: 'bg-slate-500',
    features: [
      { name: 'Up to 2 users', included: true },
      { name: 'Basic softphone', included: true },
      { name: '30 day call history', included: true },
      { name: 'Queue monitoring', included: false },
      { name: 'Analytics', included: false },
      { name: 'Priority support', included: false },
    ]
  },
  pro: {
    name: 'Pro',
    price: 20,
    color: 'bg-cyan-500',
    features: [
      { name: 'Unlimited users', included: true },
      { name: 'Full softphone features', included: true },
      { name: 'Unlimited call history', included: true },
      { name: 'Advanced queue monitoring', included: true },
      { name: 'Full analytics & reporting', included: true },
      { name: 'AI Transcripts & Summaries', included: true },
      { name: 'Global Contacts Directory', included: true },
      { name: 'Priority support', included: true },
    ]
  }
};

export default function PlanManagement() {
  const navigate = useNavigate();
  const { token, user, isCustomerAdmin } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Redirect non-admins
    if (!isCustomerAdmin()) {
      navigate('/app/dialer');
      return;
    }
    fetchCompanyDetails();
  }, [isCustomerAdmin, navigate]);

  const fetchCompanyDetails = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/company/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany(res.data);
    } catch (error) {
      console.error('Failed to fetch company details');
    } finally {
      setLoading(false);
    }
  };

  // Map old plans to new 2-tier model
  const mapPlanToCurrentModel = (plan) => {
    if (plan === 'standard' || plan === 'professional') {
      return 'pro';  // Map old tiers to Pro
    }
    return plan || 'free';
  };

  const currentPlan = mapPlanToCurrentModel(company?.plan);
  const currentPlanDetails = PLAN_FEATURES[currentPlan] || PLAN_FEATURES.free;

  const handlePlanChange = (planId) => {
    if (planId === currentPlan) return;
    setSelectedPlan(planId);
    setShowChangeDialog(true);
  };

  const confirmPlanChange = async () => {
    setProcessing(true);
    try {
      // In a real implementation, this would trigger Stripe checkout or subscription change
      await axios.put(`${API_URL}/api/company/plan`, 
        { plan: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Plan changed to ${PLAN_FEATURES[selectedPlan].name}`);
      setShowChangeDialog(false);
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setProcessing(true);
    try {
      await axios.put(`${API_URL}/api/company/plan`, 
        { plan: 'free' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription cancelled. You are now on the Free plan.');
      setShowCancelDialog(false);
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getPlanAction = (planId) => {
    if (planId === currentPlan) return 'current';
    const planOrder = ['free', 'pro'];
    const currentIndex = planOrder.indexOf(currentPlan);
    const targetIndex = planOrder.indexOf(planId);
    return targetIndex > currentIndex ? 'upgrade' : 'downgrade';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="plan-management-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-foreground">Plan Management</h1>
        <p className="text-muted-foreground mt-1">Manage your subscription and billing</p>
      </div>

      {/* Current Plan Card */}
      <Card className="border-border/50 mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {company?.name || 'Your Company'}
              </CardTitle>
              <CardDescription>Current subscription details</CardDescription>
            </div>
            <Badge className={`${currentPlanDetails.color} text-white text-lg px-4 py-1`}>
              {currentPlanDetails.name} Plan
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Crown className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Cost</p>
                <p className="text-xl font-bold">
                  {currentPlanDetails.price === 0 ? 'Free' : `$${currentPlanDetails.price}/user`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Users</p>
                <p className="text-xl font-bold">{company?.user_count || 1}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Billing Cycle</p>
                <p className="text-xl font-bold">Monthly</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan Options */}
      <h2 className="text-xl font-semibold mb-4">Available Plans</h2>
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mb-8">
        {Object.entries(PLAN_FEATURES).map(([planId, plan]) => {
          const action = getPlanAction(planId);
          const isCurrent = action === 'current';
          
          return (
            <Card 
              key={planId} 
              className={`border-border/50 relative ${isCurrent ? 'ring-2 ring-primary' : ''}`}
            >
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary">Current Plan</Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${plan.color}`}></div>
                  {plan.name}
                </CardTitle>
                <CardDescription>
                  <span className="text-2xl font-bold text-foreground">
                    {plan.price === 0 ? 'Free' : `$${plan.price}`}
                  </span>
                  {plan.price > 0 && <span className="text-muted-foreground">/user/month</span>}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground/50" />
                      )}
                      <span className={feature.included ? '' : 'text-muted-foreground/50'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                {isCurrent ? (
                  <Button variant="outline" className="w-full" disabled>
                    Current Plan
                  </Button>
                ) : (
                  <Button 
                    className={`w-full ${action === 'upgrade' ? 'bg-primary' : 'bg-secondary'}`}
                    variant={action === 'upgrade' ? 'default' : 'secondary'}
                    onClick={() => handlePlanChange(planId)}
                  >
                    {action === 'upgrade' ? 'Upgrade' : 'Downgrade'} to {plan.name}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Cancel Subscription */}
      {currentPlan !== 'free' && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Cancel Subscription
            </CardTitle>
            <CardDescription>
              Downgrade to the free plan and cancel your subscription
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              If you cancel your subscription, you will lose access to premium features at the end of your current billing period. 
              Your data will be preserved, but some functionality may be limited.
            </p>
            <Button 
              variant="destructive" 
              onClick={() => setShowCancelDialog(true)}
            >
              Cancel Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Change Plan Dialog */}
      <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Plan Change</DialogTitle>
            <DialogDescription>
              {selectedPlan && (
                <>
                  You are about to {getPlanAction(selectedPlan)} to the{' '}
                  <strong>{PLAN_FEATURES[selectedPlan]?.name}</strong> plan.
                  {getPlanAction(selectedPlan) === 'downgrade' && (
                    <p className="mt-2 text-amber-500">
                      Note: Downgrading may result in loss of some features.
                    </p>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmPlanChange} disabled={processing}>
              {processing ? 'Processing...' : 'Confirm Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Cancel Subscription?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You will be downgraded to the Free plan 
              and lose access to premium features.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Subscription
            </Button>
            <Button variant="destructive" onClick={handleCancelSubscription} disabled={processing}>
              {processing ? 'Processing...' : 'Yes, Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
